import { Button, Link } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { RoomGalleryButton } from "@/features/hotels/components/RoomSelection/HotelGalleryButton";
import { RoomDetails } from "@/features/hotels/components/RoomSelection/RoomDetails";
import {
  MealFeature,
  RoomFeature,
} from "@/features/hotels/components/RoomSelection/RoomFeature";
import type {
  HotelBookingRoomOption,
  HotelData,
  HotelOfferRoom,
} from "@/features/hotels/types/types";
import { createRoomCombinations } from "@/features/hotels/utils/roomCombinations";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

type Props = {
  hotel: HotelData;
  rooms: HotelOfferRoom[];
  guestCount: number;
  bookingNights: number;
  onRoomSelected: (option: HotelBookingRoomOption[]) => void;
  onRoomGalleryClick: (room: HotelBookingRoomOption) => void;
};

export const HotelRoomSelection = ({
  hotel,
  rooms,
  guestCount,
  bookingNights,
  onRoomSelected,
  onRoomGalleryClick,
}: Props) => {
  const t = useTranslations();
  const [selectedRoomDetails, setSelectedRoomDetails] =
    useState<HotelBookingRoomOption | null>(null);
  const { maxAvailableInstallments, isEligible, isEligibilityLoading } =
    usePaymentContext();
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);

  const combinationGroups = useMemo(
    () => createRoomCombinations(rooms),
    [rooms],
  );

  return (
    <>
      <ul className="space-y-3">
        {combinationGroups.map((roomGroup) => {
          const rooms = roomGroup.combinations.flatMap((c) => c.rooms);
          const firstRoomWithImage = rooms.find(
            (r) => r.images && r.images.length > 0,
          );

          const mainImage = firstRoomWithImage
            ? firstRoomWithImage.images?.[0]
            : null;

          return (
            <li
              key={roomGroup.id}
              className="rounded-3xl bg-white pb-4 text-text-primary"
            >
              <div className="mb-3 flex p-1">
                {firstRoomWithImage && mainImage ? (
                  <RoomGalleryButton
                    onClick={() => onRoomGalleryClick(firstRoomWithImage)}
                    thumbnailImage={mainImage}
                    imageCount={firstRoomWithImage?.images?.length || 0}
                  />
                ) : null}

                <div className="overflow-hidden p-3">
                  <div className="mb-1 line-clamp-2 text-lg font-medium">
                    {roomGroup.displayName}
                  </div>

                  <div className="mb-2 text-sm text-text-tertiary">
                    {t("hotels.guestsAndNights", {
                      guests: guestCount,
                      nights: bookingNights,
                    })}
                  </div>

                  <Link
                    onClick={() => setSelectedRoomDetails(rooms[0])}
                    component="button"
                    sx={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "var(--color-surface-accent-strong)",
                    }}
                  >
                    {t("hotels.seeRoomDetails")}
                  </Link>
                </div>
              </div>
              <ul className="flex w-full snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto px-3">
                {roomGroup.combinations.map((combination) => (
                  <li
                    key={combination.id}
                    className="flex w-55 shrink-0 snap-center flex-col rounded-2xl border border-stroke-neutral-soft p-4"
                  >
                    <ul className="mb-6 space-y-2">
                      <li>
                        {combination.isRefundable ? (
                          <RoomFeature
                            label={t("hotels.refundable")}
                            isIncluded
                          />
                        ) : (
                          <RoomFeature
                            label={t("hotels.nonRefundable")}
                            isIncluded={false}
                          />
                        )}
                      </li>
                      <li>
                        <MealFeature board={combination.boardBasis} />
                      </li>
                    </ul>

                    <div className="mt-auto mb-4">
                      <div className="w-full overflow-hidden text-base font-bold overflow-ellipsis">
                        {formatCurrency(
                          combination.totalPrice,
                          combination.currency,
                        )}
                      </div>
                      {isBnplFeatureEnabled && (
                        <div className="overflow-hidden text-sm font-medium overflow-ellipsis">
                          {t("payment.orMonthly", {
                            price: formatCurrency(
                              calculateMonthlyInstallment(
                                includeBnplFee(combination.totalPrice),
                                maxAvailableInstallments,
                              ),
                              combination.currency,
                            ),
                          })}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-text-tertiary">
                        {t("payment.totalWithTaxes")}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="contained"
                      disableElevation
                      fullWidth
                      onClick={() => {
                        onRoomSelected(combination.rooms);
                      }}
                      loading={isEligibilityLoading}
                    >
                      {t("select")}
                    </Button>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>

      {selectedRoomDetails ? (
        <SwipeableDrawer
          open
          hasFullHeight
          heightOffset={24}
          onClose={() => setSelectedRoomDetails(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedRoomDetails(null)}
            title={t("hotels.roomDetails")}
          />
          <RoomDetails option={selectedRoomDetails} hotelName={hotel.name} />
        </SwipeableDrawer>
      ) : null}
    </>
  );
};
